import { defineStore } from "pinia";

import sampleDb from "@/data/sample-db.json";
import {
  createDatabase,
  DatabaseService,
  SyncOptions,
} from "@/services/database";
import {
  hydrateDocument,
  validateDocument,
  Document,
  DocumentValidationError,
} from "@/validation";

function tryHydrateDocument(rawDoc: unknown): Document | null {
  try {
    return hydrateDocument(rawDoc);
  } catch (error) {
    if (error instanceof DocumentValidationError) {
      console.warn(
        `Skipping invalid document ${error.documentId}: ${error.message}`,
      );
      console.log(error);
    } else if (error instanceof Error) {
      console.warn(`Skipping document: ${error.message}`);
    }
    return null;
  }
}

export type SyncStatus = "off" | "active" | "idle" | "error" | "denied";

interface DocumentState {
  allDocs: Document[];
  loading: boolean;
  initialized: boolean;
  syncStatus: SyncStatus;
  syncError: string | null;
}

let db: DatabaseService | null = null;
let databaseName: string | null = null;
let changesHandler: PouchDB.Core.Changes<object> | null = null;
let syncHandler: PouchDB.Replication.Sync<object> | null = null;

export const useDocumentStore = defineStore("documents", {
  state: (): DocumentState => ({
    allDocs: [],
    loading: false,
    initialized: false,
    syncStatus: "off",
    syncError: null,
  }),
  actions: {
    async init(dbName: string) {
      if (db) {
        this.teardown();
      }
      databaseName = dbName;
      db = createDatabase(dbName);
      await this.fetchAll();
      this.listen();
      this.initialized = true;
    },

    async teardown() {
      if (changesHandler) {
        changesHandler.cancel();
        changesHandler = null;
      }
      this.stopSync();
      db = null;
      databaseName = null;
      this.initialized = false;
      this.allDocs = [];
    },

    async fetchAll() {
      if (!db) throw new Error("Database not initialized");
      try {
        this.loading = true;
        const rawDocs = await db.allDocs();
        this.allDocs = rawDocs
          .map(doc => tryHydrateDocument(doc))
          .filter((doc): doc is Document => doc !== null);
      } finally {
        this.loading = false;
      }
    },

    async put<T extends Document>(doc: T): Promise<T> {
      if (!db) throw new Error("Database not initialized");
      const now = new Date();
      const validatedDoc = validateDocument({
        ...doc,
        createdAt: doc.createdAt ?? now,
        updatedAt: now,
      });
      return db.put(validatedDoc);
    },

    async remove<T extends Document>(doc: T): Promise<T> {
      if (!db) throw new Error("Database not initialized");
      return db.remove(doc);
    },

    listen() {
      if (!db) throw new Error("Database not initialized");
      if (changesHandler) {
        changesHandler.cancel();
      }
      changesHandler = db.onChange(
        (change: { deleted: boolean; id: string; doc: unknown }) => {
          if (change.deleted) {
            this.allDocs = this.allDocs.filter(
              (doc: Document) => doc._id !== change.id,
            );
          } else {
            const hydratedDoc = tryHydrateDocument(change.doc);
            if (!hydratedDoc) return;
            const index = this.allDocs.findIndex(
              (doc: Document) => doc._id === change.id,
            );
            if (index !== -1) {
              this.allDocs[index] = hydratedDoc;
            } else {
              this.allDocs.push(hydratedDoc);
            }
          }
        },
      );
    },

    startSync(options: Omit<SyncOptions, "dbName">) {
      if (!db || !databaseName) throw new Error("Database not initialized");
      this.stopSync();
      this.syncStatus = "active";
      this.syncError = null;

      syncHandler = db.setupSync({ ...options, dbName: databaseName });

      syncHandler
        .on("active", () => {
          this.syncStatus = "active";
          this.syncError = null;
        })
        .on("paused", (err: unknown) => {
          if (err) {
            this.syncStatus = "error";
            this.syncError =
              err instanceof Error ? err.message : JSON.stringify(err);
          } else {
            this.syncStatus = "idle";
          }
        })
        .on("denied", (err: unknown) => {
          this.syncStatus = "denied";
          this.syncError = err instanceof Error ? err.message : String(err);
        })
        .on("error", (err: unknown) => {
          this.syncStatus = "error";
          this.syncError = err instanceof Error ? err.message : String(err);
        });
    },

    stopSync() {
      if (syncHandler) {
        syncHandler.cancel();
        syncHandler = null;
      }
      this.syncStatus = "off";
      this.syncError = null;
    },

    async loadSampleData(): Promise<void> {
      if (this.allDocs.length === 0) {
        (sampleDb as unknown[])
          .map(doc => tryHydrateDocument(doc))
          .filter((doc): doc is Document => doc !== null)
          .forEach(this.put);
      }
    },
  },
});
